<template>
  <div class="clearfix" v-if="!loader">
    <div class="spinner-grow text-danger" style="width: 3rem; height: 3rem;margin-top:25vh; margin-left:42%; margin-bottom:5vh" role="status">
      <span class="visually-hidden">{{ $t('doubleEntries.loading') }}</span>
    </div>
  </div>

  <div v-if="loader" class="card mb-25 border-0 rounded-0 bg-white letter-spacing" id="back">
    <!-- ═══ En-tête : titre + recherche + bouton filtres ═══ -->
    <div
      class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25 flex-wrap gap-10"
    >
      <div>
        <h5 class="mb-0 fw-bold text-black">{{ $t('doubleEntries.title') }}</h5>
        <p v-if="total > 0" class="mb-0 mt-5 fs-13 text-body-tertiary">
          {{ $t('doubleEntries.total_entries', { count: formatNumber(total) }) }}
        </p>
      </div>
      <div class="double-entries-actions">
        <!-- Refresh : vide aussi la barre de recherche -->
        <button
          class="action-icon-btn refresh-btn"
          type="button"
          :disabled="tableLoading"
          :title="$t('doubleEntries.refresh')"
          :aria-label="$t('doubleEntries.refresh')"
          @click="refreshAndClear"
        >
          <i class="flaticon-refresh"></i>
        </button>

        <!-- Recherche : croix quand il y a du texte, loupe quand vide -->
        <div class="double-entries-search">
          <input
            v-model="searchQuery"
            type="text"
            class="double-entries-search-input"
            :placeholder="$t('doubleEntries.search_placeholder')"
            @keyup.enter="searchDescription"
          />

          <button
            v-if="searchQuery"
            type="button"
            class="double-entries-clear-btn"
            :aria-label="$t('doubleEntries.clear_search')"
            :title="$t('doubleEntries.clear_search')"
            @click="clearSearch"
          >
            <i class="flaticon-cancel"></i>
          </button>

          <button
            v-else
            type="button"
            class="double-entries-search-btn"
            :aria-label="$t('doubleEntries.search_placeholder')"
            @click="searchDescription"
          >
            <i class="flaticon-search-interface-symbol"></i>
          </button>
        </div>

        <button
          class="action-icon-btn filter-btn"
          type="button"
          :title="$t('doubleEntries.filters')"
          :aria-label="$t('doubleEntries.filters')"
          @click="openFiltersModal"
        >
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>
          <span v-if="activeFiltersCount > 0" class="filter-badge">
            {{ activeFiltersCount }}
          </span>
        </button>
      </div>
    </div>

    <!-- ═══ Tableau ═══ -->
    <div class="card-body p-15 p-sm-20 p-md-25">
      <div class="table-responsive" :class="{ 'table-loading': tableLoading }">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 ps-0">{{ $t('doubleEntries.date') }}</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('doubleEntries.reference') }}</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('doubleEntries.debit') }}</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('doubleEntries.credit') }}</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">{{ $t('doubleEntries.amount') }} (XOF)</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('doubleEntries.entry_type') }}</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('doubleEntries.entity') }}</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('doubleEntries.status') }}</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">
                <span class="visually-hidden">{{ $t('doubleEntries.actions') }}</span>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="row in rows" :key="row.code">
              <td class="shadow-none lh-1 fw-medium text-black-emphasis ps-0 fs-md-15 fs-lg-16">
                {{ formatDateTimeFr(row.createdAt) }}
              </td>

              <td class="shadow-none lh-1 fw-medium">
                <button
                  type="button"
                  class="ref-btn"
                  :title="`${row.code}\n${$t('doubleEntries.copy_reference')}`"
                  @click="copyText(row.code)"
                >
                  {{ shortRef(row.code) }}
                </button>
              </td>

              <td class="shadow-none lh-1 fw-medium text-black-emphasis fs-md-15 fs-lg-16">
                <span :title="row.debit.raw">{{ row.debit.label }}</span>
              </td>

              <td class="shadow-none lh-1 fw-medium text-black-emphasis fs-md-15 fs-lg-16">
                <span :title="row.credit.raw">{{ row.credit.label }}</span>
              </td>

              <td class="shadow-none lh-1 text-end">
                <span class="fs-6 fw-bold text-black">{{ formatAmount(row.amount) }}</span>
              </td>

              <td class="shadow-none lh-1 fw-medium">
                <span class="badge" :class="entryTypeClass(row.entryType)">{{ entryTypeLabel(row.entryType) }}</span>
              </td>

              <td class="shadow-none lh-1 fw-medium">
                <span class="badge text-outline-primary">{{ row.entity }}</span>
              </td>

              <td class="shadow-none lh-1 fw-medium">
                <span class="badge" :class="adjustmentClass(row.adjustmentType)">{{ adjustmentLabel(row.adjustmentType) }}</span>
              </td>

              <td class="shadow-none lh-1 fw-medium text-end pe-0">
                <button
                  v-if="current_permissions('show_double_entry')"
                  type="button"
                  class="dot-btn lh-1 position-relative bg-transparent border-0 shadow-none p-0 transition d-inline-flex align-items-center"
                  :title="$t('doubleEntries.view_detail')"
                  :aria-label="$t('doubleEntries.view_detail')"
                  @click="openDetail(row)"
                >
                  <i class="flaticon-view fs-16"></i>
                </button>
              </td>
            </tr>

            <tr v-if="!rows.length">
              <td colspan="9" class="text-center text-body-tertiary py-25">
                {{ $t('doubleEntries.no_entries') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="total > 0" class="pagination-area d-md-flex mt-15 mt-md-25 mb-0 justify-content-between align-items-center">
        <p class="mb-0 text-paragraph">
          {{ $t('doubleEntries.showing', { count: entries.length }) }} <span class="fw-bold">{{ formatNumber(total) }}</span>
        </p>

        <nav class="mt-15 mt-md-0">
          <ul class="pagination mb-0">
            <li v-if="page > 1" class="page-item">
              <a class="page-link" href="javascript:void(0);" aria-label="Previous" @click="goToPage(page - 1)">
                <i class="flaticon-chevron-1"></i>
              </a>
            </li>

            <li v-for="(p, idx) in visiblePages" :key="`${p}-${idx}`" class="page-item" :class="{ disabled: p === '…' }">
              <span v-if="p === '…'" class="page-link">…</span>
              <a v-else-if="p === page" class="page-link active" href="javascript:void(0);">{{ p }}</a>
              <a v-else class="page-link" href="javascript:void(0);" @click="goToPage(p as number)">{{ p }}</a>
            </li>

            <li v-if="page < numPages" class="page-item">
              <a class="page-link" href="javascript:void(0);" aria-label="Next" @click="goToPage(page + 1)">
                <i class="flaticon-chevron"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>

  <!-- ═══ Modal Filtres ═══ -->
  <div class="modal fade" id="doubleEntriesFiltersModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-fullscreen-sm-down">
      <div class="modal-content">
        <form @submit.prevent="applyFilters">
          <div class="modal-header">
            <h1 class="modal-title fs-5">{{ $t('doubleEntries.filters_title') }}</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body">
            <div class="row g-3">
              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.reference') }}</label>
                <input v-model="filters.code" type="text" class="form-control shadow-none fw-medium text-black fs-md-15" />
              </div>
              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.description') }}</label>
                <input v-model="filters.description" type="text" class="form-control shadow-none fw-medium text-black fs-md-15" />
              </div>

              <!-- <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.external_reference') }}</label>
                <input v-model="filters.externalReference" type="text" class="form-control shadow-none fw-medium text-black fs-md-15" />
              </div> -->

              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.entity_type') }}</label>
                <input v-model="filters.entityType" type="text" class="form-control shadow-none fw-medium text-black fs-md-15" placeholder="payout" />
              </div>
              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.entity_code') }}</label>
                <input v-model="filters.entityCode" type="text" class="form-control shadow-none fw-medium text-black fs-md-15" />
              </div>

              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.entry_type') }}</label>
                <select v-model="filters.entryType" class="form-select fs-md-15 text-black shadow-none">
                  <option value="">{{ $t('doubleEntries.all') }}</option>
                  <option value="REAL">{{ $t('doubleEntries.real') }}</option>
                  <option value="VIRTUAL">{{ $t('doubleEntries.virtual') }}</option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.currency') }}</label>
                <input v-model="filters.currency" type="text" class="form-control shadow-none fw-medium text-black fs-md-15" placeholder="XOF" />
              </div>

              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.date_from') }}</label>
                <input v-model="filters.dateFrom" type="date" class="form-control shadow-none fw-medium text-black fs-md-15" />
              </div>
              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.date_to') }}</label>
                <input v-model="filters.dateTo" type="date" class="form-control shadow-none fw-medium text-black fs-md-15" />
              </div>

              <div class="col-12 col-md-4">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.exact_amount') }}</label>
                <input v-model="filters.amount" type="number" min="0" step="any" class="form-control shadow-none fw-medium text-black fs-md-15" />
              </div>
              <div class="col-6 col-md-4">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.min_amount') }}</label>
                <input v-model="filters.minAmount" type="number" min="0" step="any" class="form-control shadow-none fw-medium text-black fs-md-15" />
              </div>
              <div class="col-6 col-md-4">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.max_amount') }}</label>
                <input v-model="filters.maxAmount" type="number" min="0" step="any" class="form-control shadow-none fw-medium text-black fs-md-15" />
              </div>

              <div class="col-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.order') }}</label>
                <select v-model="filters.ordre" class="form-select fs-md-15 text-black shadow-none">
                  <option value="DESC">{{ $t('doubleEntries.order_desc') }}</option>
                  <option value="ASC">{{ $t('doubleEntries.order_asc') }}</option>
                </select>
              </div>
              <div class="col-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.per_page') }}</label>
                <select v-model.number="filters.count" class="form-select fs-md-15 text-black shadow-none">
                  <option :value="10">10</option>
                  <option :value="20">20</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary me-auto" @click="resetFilters">{{ $t('doubleEntries.reset') }}</button>
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('doubleEntries.cancel') }}</button>
            <button type="submit" :disabled="tableLoading" class="btn text-white" style="background-color: #EB9355;">
              <span style="color: #FFF;">{{ $t('doubleEntries.apply') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- ═══ Modal Détail de l'écriture ═══ -->
  <div class="modal fade" id="entryDetailModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-fullscreen-sm-down">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5">{{ $t('doubleEntries.detail_title') }} ({{ detail?.entryType?.toUpperCase() ?? '' }})</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <!-- ═══ Onglets : Infos principales / Infos avancées ═══ -->
        <div class="modal-tabs" v-if="!detailLoading && !detailError && detail">
          <button
            type="button"
            class="modal-tab"
            :class="{ active: activeDetailTab === 'main' }"
            @click="activeDetailTab = 'main'"
          >
            Informations principales
          </button>
          <button
            type="button"
            class="modal-tab"
            :class="{ active: activeDetailTab === 'advanced' }"
            @click="activeDetailTab = 'advanced'"
          >
            Informations avancées
          </button>
        </div>

        <div class="modal-body">
          <!-- Chargement -->
          <div v-if="detailLoading" class="detail-state">
            <div class="spinner-border text-danger" role="status">
              <span class="visually-hidden">{{ $t('doubleEntries.loading') }}</span>
            </div>
          </div>

          <!-- Erreur -->
          <div v-else-if="detailError" class="detail-state flex-column gap-10">
            <p class="mb-0 text-body-tertiary">{{ $t('doubleEntries.error_loading_detail') }}</p>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="fetchDetail(currentDetailCode)">
              {{ $t('doubleEntries.retry') }}
            </button>
          </div>

          <!-- Contenu -->
          <template v-else-if="detail">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">

              <!-- ═══ Onglet 1 : Informations principales ═══ -->
              <div v-show="activeDetailTab === 'main'">
                <div class="row g-3">
                  <div class="col-12 col-md-3" v-if="hasValue(detail.code)">
                    <label class="d-block text-black fw-semibold mb-10">Code écriture</label>
                    <input v-model="detail.code" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>

                  <div class="col-12 col-md-3" v-if="hasValue(detail.amount)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.amount') }}</label>
                    <input :value="`${formatAmount(detail.amount)} ${detail.currency}`" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>

                  <div class="col-12 col-md-3" v-if="hasValue(detail.createdAt)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.date') }}</label>
                    <input :value="formatDateTimeFr(detail.createdAt, true)" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>

                  <div class="col-12 col-md-3" v-if="hasValue(detail.currency)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.currency') }}</label>
                    <input v-model="detail.currency" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>

                  <div class="col-12 col-md-3" v-if="hasValue(detail.entryType)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.entry_type') }}</label>
                    <input :value="entryTypeLabel(detail.entryType)" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>

                  <div class="col-12 col-md-3" v-if="hasValue(detail.adjustmentType)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.status') }}</label>
                    <input :value="adjustmentLabel(detail.adjustmentType)" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>

                  <div class="col-12 col-md-3" v-if="hasValue(detail.entity)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.entity') }}</label>
                    <input v-model="detail.entity" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>

                  <div class="col-12 col-md-3" v-if="hasValue(detail.entityCode)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.entity_code') }}</label>
                    <input v-model="detail.entityCode" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>

                  <div class="col-12" v-if="hasValue(detail.description)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.description') }}</label>
                    <textarea :value="detail.description" rows="2" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly></textarea>
                  </div>
                </div>

                <!-- ═══ Mouvements comptables (tableau débit/crédit unifié) ═══ -->
                <h6 class="detail-section-title">{{ $t('doubleEntries.detail_ledger_entries') }}</h6>
                <div class="ledger-wrapper">
                  <table class="ledger-table">
                    <thead>
                      <tr>
                        <th class="ledger-col-side">Débit</th>
                        <th class="ledger-col-amount">Montant</th>
                        <th class="ledger-col-side">Crédit</th>
                        <th class="ledger-col-amount">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, idx) in ledgerTableRows" :key="idx">
                        <td class="ledger-account-cell">
                          <div class="ledger-account-name">{{ row.debitAccount }}</div>
                          <div v-if="row.debitCode" class="ledger-account-code">
                            <span class="ledger-code-text">{{ row.debitCode }}</span>
                            <button type="button" class="copy-btn-mini" @click="copyText(row.debitCode)">Copier</button>
                          </div>
                        </td>
                        <td class="ledger-amount-cell">
                          <span class="ledger-amount-value">{{ formatAmount(row.debitAmount) }}</span>
                          <span class="ledger-amount-currency">{{ row.currency }}</span>
                        </td>
                        <td class="ledger-account-cell">
                          <div class="ledger-account-name">{{ row.creditAccount }}</div>
                          <div v-if="row.creditCode" class="ledger-account-code">
                            <span class="ledger-code-text">{{ row.creditCode }}</span>
                            <button type="button" class="copy-btn-mini" @click="copyText(row.creditCode)">Copier</button>
                          </div>
                        </td>
                        <td class="ledger-amount-cell">
                          <span class="ledger-amount-value">{{ formatAmount(row.creditAmount) }}</span>
                          <span class="ledger-amount-currency">{{ row.currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="!ledgerTableRows.length">
                        <td colspan="4" class="text-center text-body-tertiary py-15">—</td>
                      </tr>
                    </tbody>
                    <tfoot v-if="ledgerTableRows.length">
                      <tr>
                        <td class="ledger-total-label">Total débit</td>
                        <td class="ledger-total-cell">
                          <span class="ledger-total-value">{{ formatAmount(totalDebit) }}</span>
                          <span class="ledger-amount-currency">{{ detail.currency }}</span>
                        </td>
                        <td class="ledger-total-label">Total crédit</td>
                        <td class="ledger-total-cell">
                          <span class="ledger-total-value">{{ formatAmount(totalCredit) }}</span>
                          <span class="ledger-amount-currency">{{ detail.currency }}</span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <!-- ═══ Onglet 2 : Informations avancées ═══ -->
              <div v-show="activeDetailTab === 'advanced'">
                <!-- Transaction associée -->
                <template v-if="transactionFields.length">
                  <h6 class="detail-section-title">{{ $t('doubleEntries.detail_transaction') }}</h6>
                  <div class="row g-3">
                    <div
                      v-for="field in transactionFields"
                      :key="field.key"
                      class="col-12 col-md-3"
                    >
                      <label class="d-block text-black fw-semibold mb-10">{{ field.label }}</label>
                      <input
                        :value="field.value"
                        type="text"
                        class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16"
                        readonly
                      />
                    </div>
                  </div>
                </template>

                <!-- Topup -->
                <template v-if="topupFields.length">
                  <h6 class="detail-section-title">{{ $t('doubleEntries.detail_topup') }}</h6>
                  <div class="row g-3">
                    <div
                      v-for="field in topupFields"
                      :key="field.key"
                      class="col-12 col-md-3"
                    >
                      <label class="d-block text-black fw-semibold mb-10">{{ field.label }}</label>
                      <input :value="field.value" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                    </div>
                  </div>
                </template>

                <!-- Remboursement -->
                <template v-if="repaymentFields.length">
                  <h6 class="detail-section-title">{{ $t('doubleEntries.detail_repayment') }}</h6>
                  <div class="row g-3">
                    <div
                      v-for="field in repaymentFields"
                      :key="field.key"
                      class="col-12 col-md-3"
                    >
                      <label class="d-block text-black fw-semibold mb-10">{{ field.label }}</label>
                      <input :value="field.value" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                    </div>
                  </div>
                </template>

                <!-- Intégrité -->
                <h6 class="detail-section-title">{{ $t('doubleEntries.detail_integrity') }}</h6>
                <div class="row g-3">
                  <div class="col-12 col-md-6" v-if="hasValue(detail.integrityHash)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.integrity_hash') }}</label>
                    <input :value="detail.integrityHash" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>
                  <div class="col-12 col-md-6" v-if="hasValue(detail.previousHash)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.previous_hash') }}</label>
                    <input :value="detail.previousHash" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>
                  <div class="col-12 col-md-6" v-if="hasValue(detail.externalReference)">
                    <label class="d-block text-black fw-semibold mb-10">{{ $t('doubleEntries.external_reference') }}</label>
                    <input :value="detail.externalReference" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
                  </div>
                </div>
              </div>

            </div>
          </template>
        </div>

        <!-- <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('doubleEntries.close') }}</button>
        </div> -->
      </div>
    </div>
  </div>

  <div id="snackbar"></div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, inject } from "vue";
import { useI18n } from "vue-i18n";
import axios from 'axios';
import { Modal } from 'bootstrap';
import { current_permissions } from '../../../utils/permission';
import router from '../../../router/index';
import { showToast, checkSessionValidate } from "@/utils/function";

const { t } = useI18n();

const backApi = inject('back');
const token = localStorage.getItem('login_token');

interface DoubleEntry {
  code: string;
  description: string;
  amount: number;
  currency: string;
  entryType: string;
  adjustmentType: string;
  createdAt: string;
  entity: string;
  entityCode: string;
}

interface AccountLabel {
  label: string;
  raw: string;
}

interface EntryRow extends DoubleEntry {
  debit: AccountLabel;
  credit: AccountLabel;
}

interface LedgerAccount {
  code: string;
  name: string;
  type: string;
  merchantAccountType: string | null;
  providerBalanceType: string | null;
}

interface LedgerEntry {
  amount: number;
  operationDirection: 'DEBIT' | 'CREDIT' | string;
  ledgerAccount: LedgerAccount;
}

interface DoubleEntryTransaction {
  checkoutId: string | null;
  payoutId: string | null;
  intBulkId: string | null;
  payoutType: number | null;
  amount: number | null;
  bulkTotalAmount: number | null;
  totalFees: number | null;
  imoneyFees: number | null;
  intOperatorBulkId: string | null;
  operatorFees: number | null;
  code: string | null;
  bankTransfertId: string | null;
  transactionType: number | null;
  bankTransfertType: string | number | null;
}

interface DoubleEntryDetail {
  code: string;
  description: string;
  amount: number;
  currency: string;
  entryType: string;
  adjustmentType: string;
  integrityHash: string | null;
  previousHash: string | null;
  externalReference: string | null;
  transaction: DoubleEntryTransaction | null;
  topup: Record<string, unknown> | null;
  repayment: Record<string, unknown> | null;
  ledgerEntries: LedgerEntry[];
  createdAt: string;
  entity: string;
  entityCode: string;
}

interface DetailField {
  key: string;
  label: string;
  value: string | number;
  copyable?: boolean;
}

interface LedgerTableRow {
  debitAccount: string;
  debitCode: string;
  debitAmount: number;
  creditAccount: string;
  creditCode: string;
  creditAmount: number;
  currency: string;
  operation: string;
}

interface Filters {
  description: string;
  code: string;
  externalReference: string;
  entityType: string;
  entityCode: string;
  entryType: string;
  currency: string;
  amount: string;
  minAmount: string;
  maxAmount: string;
  dateFrom: string;
  dateTo: string;
  ordre: 'ASC' | 'DESC';
  count: number;
}

const doubleEntriesUrl = backApi + '/secure/ledger/double-entries';

const loader = ref(false);
const tableLoading = ref(false);

const entries = ref(new Array<DoubleEntry>());
const page = ref(1);
const total = ref(0);
const numPages = ref(1);

// ═══ Utilitaires (déclarés avant les computed qui les utilisent) ═══
function hasValue(value: any): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '—' || trimmed === 'null' || trimmed === 'undefined') return false;
  }
  if (typeof value === 'number' && isNaN(value)) return false;
  return true;
}

function formatNumber(value: number): string {
  return Number(value || 0).toLocaleString('fr-FR').replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ');
}

function formatAmount(value: any): string {
  const num = Number(value ?? 0);
  if (isNaN(num)) return '0';
  return num
    .toLocaleString('fr-FR', { maximumFractionDigits: 4 })
    .replace(/\u202f/g, ' ')
    .replace(/\u00a0/g, ' ');
}

function formatDateTimeFr(value: string | null | undefined, withSeconds = false) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const base = `${day}/${month}/${year} ${hours}:${minutes}`;
  return withSeconds ? `${base}:${String(d.getSeconds()).padStart(2, '0')}` : base;
}

function shortRef(code: string): string {
  if (!code) return '';
  return code.length > 8 ? `${code.slice(0, 8)}…` : code;
}

function showModal(id: string) {
  const el = document.getElementById(id);
  if (el) Modal.getOrCreateInstance(el).show();
}
function hideModal(id: string) {
  const el = document.getElementById(id);
  if (el) Modal.getInstance(el)?.hide();
}

// ═══ Filtres ═══
const defaultFilters = (): Filters => ({
  description: '',
  code: '',
  externalReference: '',
  entityType: '',
  entityCode: '',
  entryType: '',
  currency: '',
  amount: '',
  minAmount: '',
  maxAmount: '',
  dateFrom: '',
  dateTo: '',
  ordre: 'DESC',
  count: 50,
});

const searchQuery = ref('');
const filters = reactive<Filters>(defaultFilters());
const appliedFilters = ref<Filters>(defaultFilters());

// Le badge de filtre exclut la description et le code car ils sont gérés
// par la barre de recherche principale (mais on les garde quand même
// comptés s'ils sont saisis dans le modal des filtres)
const FILTER_KEYS = [
  'description', 'code', 'externalReference', 'entityType', 'entityCode',
  'entryType', 'currency', 'amount', 'minAmount', 'maxAmount', 'dateFrom', 'dateTo',
] as const;

const activeFiltersCount = computed(
  () => FILTER_KEYS.filter((key) => String(appliedFilters.value[key]).trim() !== '').length
);

function buildParams(pageArg: number) {
  const f = appliedFilters.value;
  const params: Record<string, string | number> = {
    page: pageArg,
    count: f.count,
    ordre: f.ordre,
  };
  FILTER_KEYS.forEach((key) => {
    const value = String(f[key]).trim();
    if (value !== '') params[key] = key === 'currency' ? value.toUpperCase() : value;
  });
  return params;
}

// ═══ Recherche principale : code OU description ═══
// Détecte si la saisie est un UUID (recherche par code) ou du texte libre
// (recherche par description). Le backend reçoit le paramètre approprié.
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function searchDescription() {
  const q = searchQuery.value.trim();

  if (!q) {
    // Champ vide : on nettoie les deux champs et on recharge
    appliedFilters.value = {
      ...appliedFilters.value,
      code: '',
      description: '',
    };
    fetchEntries(1);
    return;
  }

  const looksLikeUuid = UUID_REGEX.test(q);

  appliedFilters.value = {
    ...appliedFilters.value,
    code: looksLikeUuid ? q : '',
    description: looksLikeUuid ? '' : q,
  };
  fetchEntries(1);
}

// Efface la barre de recherche et relance la liste complète
function clearSearch() {
  searchQuery.value = '';
  appliedFilters.value = {
    ...appliedFilters.value,
    code: '',
    description: '',
  };
  fetchEntries(1);
}

// Refresh : vide aussi la barre de recherche
function refreshAndClear() {
  searchQuery.value = '';
  appliedFilters.value = defaultFilters();
  Object.assign(filters, defaultFilters());
  fetchEntries(1);
}

function openFiltersModal() {
  Object.assign(filters, appliedFilters.value);
  showModal('doubleEntriesFiltersModal');
}

function applyFilters() {
  if (filters.minAmount !== '' && filters.maxAmount !== '' && Number(filters.minAmount) > Number(filters.maxAmount)) {
    showToast(t('doubleEntries.error_amount_range'), "red");
    return;
  }
  if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
    showToast(t('doubleEntries.error_date_range'), "red");
    return;
  }
  appliedFilters.value = { ...filters };
  searchQuery.value = filters.code || filters.description || '';
  hideModal('doubleEntriesFiltersModal');
  fetchEntries(1);
}

function resetFilters() {
  Object.assign(filters, defaultFilters());
  searchQuery.value = '';
  appliedFilters.value = defaultFilters();
  hideModal('doubleEntriesFiltersModal');
  fetchEntries(1);
}

// ═══ Parsing de la description (pour le tableau principal) ═══
const TRANSFER_REGEX = /\sdu\s+(.+?)\s+vers\s+(?:le|la|les|l')\s*(.+)$/i;
const SMALL_WORDS = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'en', 'et', 'by']);

function prettifyAccount(raw: string): string {
  const cleaned = raw.replace(/^COMPTE\s+(DE\s+|DU\s+|D')?/i, '').trim();
  if (!cleaned) return raw;

  return cleaned
    .split(/\s+/)
    .map((word, i) => {
      if (word === '-') return word;
      const lower = word.toLowerCase();
      if (i > 0 && SMALL_WORDS.has(lower)) return lower;
      if (word.length <= 2) return word.toUpperCase();
      return word
        .split('-')
        .map((part) => (part.length <= 1 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()))
        .join('-');
    })
    .join(' ');
}

function parseAccounts(description: string): { debit: AccountLabel; credit: AccountLabel } {
  const empty: AccountLabel = { label: '—', raw: description ?? '' };
  const match = (description ?? '').match(TRANSFER_REGEX);
  if (!match) return { debit: empty, credit: empty };

  const debitRaw = match[1].trim();
  const creditRaw = match[2].trim();
  return {
    debit: { label: prettifyAccount(debitRaw), raw: debitRaw },
    credit: { label: prettifyAccount(creditRaw), raw: creditRaw },
  };
}

const rows = computed<EntryRow[]>(() =>
  entries.value.map((entry) => {
    const { debit, credit } = parseAccounts(entry.description);
    return { ...entry, debit, credit };
  })
);

// ═══ Modal Détail ═══
const detail = ref<DoubleEntryDetail | null>(null);
const detailLoading = ref(false);
const detailError = ref(false);
const currentDetailCode = ref('');

const activeDetailTab = ref<'main' | 'advanced'>('main');

function openDetail(row: EntryRow) {
  showModal('entryDetailModal');
  activeDetailTab.value = 'main';
  fetchDetail(row.code);
}

async function fetchDetail(code: string) {
  if (!code) return;

  currentDetailCode.value = code;
  detail.value = null;
  detailError.value = false;
  detailLoading.value = true;

  try {
    const response = await axios.get(`${doubleEntriesUrl}/${encodeURIComponent(code)}/show`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (currentDetailCode.value !== code) return;
    detail.value = (response.data?.data ?? response.data) as DoubleEntryDetail;
  } catch (error: any) {
    if (currentDetailCode.value !== code) return;
    detailError.value = true;
    if (error?.response?.status === 401) hideModal('entryDetailModal');
    handleAxiosError(error, t('doubleEntries.error_loading_detail'));
  } finally {
    if (currentDetailCode.value === code) detailLoading.value = false;
  }
}

// ── Transaction associée (filtrée directement dans la computed) ──
const transactionFields = computed<DetailField[]>(() => {
  const tx = detail.value?.transaction;
  if (!tx) return [];

  const defs: { key: keyof DoubleEntryTransaction; label: string; kind?: 'amount' }[] = [
    { key: 'code', label: t('doubleEntries.tx_code') },
    { key: 'payoutId', label: t('doubleEntries.tx_payout_id') },
    { key: 'checkoutId', label: t('doubleEntries.tx_checkout_id') },
    { key: 'intBulkId', label: t('doubleEntries.tx_int_bulk_id') },
    { key: 'intOperatorBulkId', label: t('doubleEntries.tx_int_operator_bulk_id') },
    { key: 'bankTransfertId', label: t('doubleEntries.tx_bank_transfert_id') },
    { key: 'transactionType', label: t('doubleEntries.tx_transaction_type') },
    { key: 'payoutType', label: t('doubleEntries.tx_payout_type') },
    { key: 'bankTransfertType', label: t('doubleEntries.tx_bank_transfert_type') },
    { key: 'amount', label: t('doubleEntries.tx_amount'), kind: 'amount' },
    { key: 'bulkTotalAmount', label: t('doubleEntries.tx_bulk_total_amount'), kind: 'amount' },
    { key: 'totalFees', label: t('doubleEntries.tx_total_fees'), kind: 'amount' },
    { key: 'imoneyFees', label: t('doubleEntries.tx_imoney_fees'), kind: 'amount' },
    { key: 'operatorFees', label: t('doubleEntries.tx_operator_fees'), kind: 'amount' },
  ];

  return defs
    .filter(({ key }) => hasValue(tx[key]))
    .map(({ key, label, kind }) => {
      const raw = tx[key];
      return {
        key,
        label,
        value: kind === 'amount'
          ? `${formatAmount(raw)} ${detail.value?.currency ?? ''}`.trim()
          : (raw as string | number),
        copyable: false,
      };
    });
});

// ── Topup / Remboursement ──
function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function genericFields(obj: Record<string, unknown> | null | undefined): DetailField[] {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .filter(([, value]) => hasValue(value))
    .map(([key, value]) => ({
      key,
      label: humanizeKey(key),
      value: typeof value === 'object' ? JSON.stringify(value) : (value as string | number),
    }));
}

const topupFields = computed(() => genericFields(detail.value?.topup));
const repaymentFields = computed(() => genericFields(detail.value?.repayment));

// ── Tableau des mouvements comptables ──
// On affiche le nom COMPLET du compte tel que fourni par l'API
// (ledgerAccount.name), sans passer par prettifyAccount.
const ledgerTableRows = computed<LedgerTableRow[]>(() => {
  const ledgerEntries = detail.value?.ledgerEntries ?? [];
  const debits = ledgerEntries.filter((e) => (e.operationDirection ?? '').toUpperCase() === 'DEBIT');
  const credits = ledgerEntries.filter((e) => (e.operationDirection ?? '').toUpperCase() === 'CREDIT');
  const rowCount = Math.max(debits.length, credits.length);
  const currency = detail.value?.currency ?? '';
  const operation = detail.value?.entity ?? '';

  return Array.from({ length: rowCount }, (_, i) => {
    const d = debits[i];
    const c = credits[i];
    return {
      debitAccount: d ? (d.ledgerAccount?.name || '—') : '—',
      debitCode: d?.ledgerAccount?.code ?? '',
      debitAmount: d ? d.amount : 0,
      creditAccount: c ? (c.ledgerAccount?.name || '—') : '—',
      creditCode: c?.ledgerAccount?.code ?? '',
      creditAmount: c ? c.amount : 0,
      currency,
      operation,
    };
  });
});

const totalDebit = computed(() => ledgerTableRows.value.reduce((sum, row) => sum + row.debitAmount, 0));
const totalCredit = computed(() => ledgerTableRows.value.reduce((sum, row) => sum + row.creditAmount, 0));

// ═══ Badges ═══
function entryTypeLabel(type: string): string {
  switch ((type ?? '').toLowerCase()) {
    case 'real': return t('doubleEntries.real');
    case 'virtual': return t('doubleEntries.virtual');
    default: return type ?? '';
  }
}
function entryTypeClass(type: string): string {
  switch ((type ?? '').toLowerCase()) {
    case 'real': return 'text-outline-success';
    case 'virtual': return 'text-outline-info';
    default: return 'text-outline-primary';
  }
}

function adjustmentLabel(type: string): string {
  if ((type ?? '').toLowerCase() === 'normal') return t('doubleEntries.normal');
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
}
function adjustmentClass(type: string): string {
  return (type ?? '').toLowerCase() === 'normal' ? 'text-outline-primary' : 'text-outline-danger';
}

// ═══ Copie ═══
async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    showToast(t('doubleEntries.copied'), "green");
  } catch {
    showToast(t('doubleEntries.error_occurred'), "red");
  }
}

// ═══ Pagination ═══
const visiblePages = computed<(number | string)[]>(() => {
  const last = numPages.value;
  const current = page.value;

  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);

  const pages: (number | string)[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(last - 1, current + 1);

  if (start > 2) pages.push('…');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < last - 1) pages.push('…');
  pages.push(last);

  return pages;
});

function goToPage(target: number) {
  if (target === page.value || target < 1 || target > numPages.value || tableLoading.value) return;
  fetchEntries(target);
}

// ═══ Appel API ═══
function handleAxiosError(error: any, fallback = t('doubleEntries.error_occurred')) {
  if (error.response) {
    if (error.response.status === 401) {
      router.push('/');
    } else {
      showToast(error.response.data?.message ?? fallback, "red");
    }
  } else {
    showToast(fallback, "red");
  }
}

async function fetchEntries(pageArg = 1, initial = false) {
  if (initial) {
    loader.value = false;
  } else {
    tableLoading.value = true;
  }

  try {
    const response = await axios.get(doubleEntriesUrl, {
      params: buildParams(pageArg),
      headers: { Authorization: `Bearer ${token}` }
    });

    entries.value = response.data.data ?? [];

    const pagination = response.data.pagination ?? {};
    page.value = Number(pagination.currentPage ?? pageArg);
    total.value = Number(pagination.total ?? entries.value.length);
    numPages.value = Number(pagination.numPages ?? 1);
  } catch (error) {
    handleAxiosError(error, t('doubleEntries.error_loading'));
  } finally {
    loader.value = true;
    tableLoading.value = false;
  }
}

onMounted(() => {
  checkSessionValidate(1);
  fetchEntries(1, true);
});
</script>

<style scoped>
.double-entries-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 0;
}
.action-icon-btn {
  position: relative;
  width: 38px;
  height: 38px;
  min-width: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #555;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.action-icon-btn:hover {
  background-color: #f5f5f5;
  color: #eb9355;
}
.action-icon-btn:focus { outline: none; }
.action-icon-btn:focus-visible {
  outline: 2px solid rgba(235, 147, 85, 0.35);
  outline-offset: 2px;
}
.action-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.refresh-btn i { font-size: 17px; line-height: 1; }

.double-entries-search {
  position: relative;
  width: 320px;
  height: 40px;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e1e1e1;
  border-radius: 7px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.double-entries-search:hover { border-color: #d5d5d5; }
.double-entries-search:focus-within {
  border-color: #eb9355;
  box-shadow: 0 0 0 2px rgba(235, 147, 85, 0.08);
}
.double-entries-search-input {
  width: 100%;
  height: 100%;
  padding: 0 44px 0 13px;
  border: 0;
  outline: none;
  background: transparent;
  color: #212529;
  font-weight: 500;
  font-size: 14px;
  line-height: 1;
}
.double-entries-search-input::placeholder { color: #999; font-weight: 400; }
.double-entries-search-input:focus { outline: none; box-shadow: none; }

/* Loupe (visible quand le champ est vide) */
.double-entries-search-btn {
  position: absolute;
  top: 50%;
  right: 5px;
  width: 32px;
  height: 32px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #777;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.double-entries-search-btn:hover { background-color: #f5f5f5; color: #eb9355; }
.double-entries-search-btn:focus { outline: none; }
.double-entries-search-btn:focus-visible {
  outline: 2px solid rgba(235, 147, 85, 0.3);
  outline-offset: 1px;
}
.double-entries-search-btn i { font-size: 14px; line-height: 1; }

/* Croix (visible quand le champ contient du texte) */
.double-entries-clear-btn {
  position: absolute;
  top: 50%;
  right: 5px;
  width: 32px;
  height: 32px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #999;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.double-entries-clear-btn:hover { background-color: #f5f5f5; color: #eb9355; }
.double-entries-clear-btn:focus { outline: none; }
.double-entries-clear-btn:focus-visible {
  outline: 2px solid rgba(235, 147, 85, 0.3);
  outline-offset: 1px;
}
.double-entries-clear-btn i { font-size: 13px; line-height: 1; }

.filter-btn { position: relative; }
.filter-btn svg { display: block; width: 19px; height: 19px; }
.filter-badge {
  position: absolute;
  top: 1px;
  right: 1px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #eb9355;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.table-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.15s ease;
}
.table-responsive { width: 100%; }
.table thead th { vertical-align: middle; white-space: nowrap; }
.table tbody td { vertical-align: middle; }

.ref-btn {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: #f6f6f6;
  border: 1px solid #eee;
  border-radius: 6px;
  color: #444;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.2;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}
.ref-btn:hover { border-color: #eb9355; background-color: #fff4ec; color: #333; }
.ref-btn:focus { outline: none; }
.ref-btn:focus-visible { outline: 2px solid #eb9355; outline-offset: 2px; }

/* ═══ Onglets du modal ═══ */
.modal-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 20px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
}
.modal-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #777;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: color .15s ease, border-color .15s ease;
  font-family: inherit;
  white-space: nowrap;
}
.modal-tab:hover:not(.active) { color: #EB9355; }
.modal-tab.active { color: #EB9355; border-bottom-color: #EB9355; }

/* ═══ Modal détail ═══ */
.detail-state {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-section-title {
  margin: 22px 0 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
  color: #000;
  font-size: 14px;
  font-weight: 700;
}

/* ═══ Tableau unifié Débit / Crédit ═══ */
.ledger-wrapper {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}
.ledger-table {
  width: 100%;
  border-collapse: collapse;
}
.ledger-table thead th {
  padding: 10px 14px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  color: #000;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
}
.ledger-table thead th.ledger-col-amount {
  text-align: right;
  width: 140px;
}
.ledger-table thead th.ledger-col-side {
  width: calc((100% - 280px) / 2);
}
.ledger-table tbody td {
  padding: 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
}
.ledger-table tbody tr:last-child td {
  border-bottom: 1px solid #e8e8e8;
}
.ledger-account-cell { max-width: 0; }
.ledger-account-name {
  color: #000;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.35;
  word-break: break-word;
  hyphens: auto;
}
.ledger-account-code {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
}
.ledger-code-text {
  min-width: 0;
  color: #000;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  word-break: break-all;
}
.ledger-amount-cell {
  text-align: right;
  white-space: nowrap;
}
.ledger-amount-value {
  color: #000;
  font-size: 14px;
  font-weight: 700;
}
.ledger-amount-currency {
  margin-left: 4px;
  color: #000;
  font-size: 11px;
  font-weight: 600;
}
.ledger-table tfoot td {
  padding: 10px 14px;
  background: #fafafa;
  border-top: 1px solid #e8e8e8;
  color: #000;
}
.ledger-total-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.ledger-total-cell {
  text-align: right;
  white-space: nowrap;
}
.ledger-total-value {
  color: #000;
  font-size: 14px;
  font-weight: 700;
}

.copy-btn-mini {
  flex-shrink: 0;
  padding: 1px 6px;
  background: transparent;
  border: 1px solid #ddd;
  border-radius: 4px;
  color: #000;
  font-size: 10.5px;
  line-height: 1.4;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}
.copy-btn-mini:hover { border-color: #eb9355; color: #000; }

.pagination .page-link { cursor: pointer; }
.pagination .page-item.disabled .page-link { cursor: default; }
.pagination .page-link:focus { box-shadow: none; }

#doubleEntriesFiltersModal .modal-body,
#entryDetailModal .modal-body {
  max-height: 75vh;
  overflow-y: auto;
}
#doubleEntriesFiltersModal .form-control {
  height: 42px;
}
#doubleEntriesFiltersModal .form-select {
  padding-top: 8px;
  padding-bottom: 8px;
  line-height: 1.5;
  min-height: 42px;
}
#doubleEntriesFiltersModal .modal-body::-webkit-scrollbar,
#entryDetailModal .modal-body::-webkit-scrollbar { width: 6px; }
#doubleEntriesFiltersModal .modal-body::-webkit-scrollbar-track,
#entryDetailModal .modal-body::-webkit-scrollbar-track { background: transparent; }
#doubleEntriesFiltersModal .modal-body::-webkit-scrollbar-thumb,
#entryDetailModal .modal-body::-webkit-scrollbar-thumb {
  background: #d8d8d8;
  border-radius: 10px;
}
#doubleEntriesFiltersModal .modal-body::-webkit-scrollbar-thumb:hover,
#entryDetailModal .modal-body::-webkit-scrollbar-thumb:hover { background: #bbb; }

.badge { white-space: nowrap; }
#snackbar { position: fixed; z-index: 9999; }

@media (max-width: 1199px) {
  .double-entries-search { width: 280px; }
  .double-entries-actions { gap: 10px; }
}
@media (max-width: 991px) {
  .double-entries-actions { margin-top: 10px; }
  .double-entries-search { width: 300px; }
}
@media (max-width: 767px) {
  .double-entries-actions {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
  }
  .double-entries-search { flex: 1; width: auto; min-width: 0; }
  .action-icon-btn { width: 36px; height: 36px; min-width: 36px; }
  .double-entries-search-input { font-size: 13px; }
  .table-responsive table { font-size: 13px; }
  .pagination-area { flex-direction: column; gap: 10px; }
  .pagination { flex-wrap: wrap; }
}
@media (max-width: 576px) {
  .modal-tab { padding: 12px 10px; font-size: 13px; }
  #doubleEntriesFiltersModal .modal-body,
  #entryDetailModal .modal-body { max-height: none; }
  #doubleEntriesFiltersModal .modal-footer { flex-wrap: wrap; gap: 8px; }
  #doubleEntriesFiltersModal .modal-footer .me-auto {
    width: 100%;
    margin-right: 0 !important;
  }
  .ledger-table thead th,
  .ledger-table tbody td,
  .ledger-table tfoot td { padding: 10px 8px; }
  .ledger-table thead th.ledger-col-amount { width: 90px; }
  .ledger-account-name { font-size: 12px; }
  .ledger-account-code { flex-wrap: wrap; }
}
@media (max-width: 420px) {
  .double-entries-actions { gap: 5px; }
  .action-icon-btn { width: 34px; height: 34px; min-width: 34px; }
  .double-entries-search { height: 38px; }
  .double-entries-search-input { padding-left: 10px; padding-right: 38px; font-size: 12px; }
  .double-entries-search-btn { right: 3px; width: 30px; height: 30px; }
  .double-entries-clear-btn { right: 3px; width: 30px; height: 30px; }
  .filter-badge { top: 0; right: 0; }
}
</style>